import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const NPS_LABELS = {
  topping_frozen: {
    nps: 'NPS Score',
    calidad_productos: 'Calidad de productos',
    variedad_productos: 'Variedad de productos',
    relacion_calidad_precio: 'Relación calidad-precio',
    profesionalismo: 'Profesionalismo',
    puntualidad_entregas: 'Puntualidad en entregas',
    atencion_cliente: 'Atención al cliente',
    servicio_postventa: 'Servicio postventa',
  },
  heladeria: {
    nps: 'NPS Score',
    sabor: 'Sabor del producto',
    presentacion: 'Presentación',
    limpieza: 'Limpieza del local',
    atencion_personal: 'Atención del personal',
    calidad_precio: 'Relación calidad-precio',
    rapidez: 'Rapidez del servicio',
  },
};

function NPSGauge({ score, promoters, passives, detractors, total }) {
  const color = score >= 50 ? '#22c55e' : score >= 0 ? '#eab308' : '#ef4444';
  return (
    <div style={{ textAlign:'center', padding:20 }}>
      <div style={{ fontSize:56, fontWeight:800, color, lineHeight:1 }}>
        {score !== null ? score : '—'}
      </div>
      <div style={{ fontSize:13, color:'#64748b', marginTop:4 }}>NPS Score</div>
      <div style={{ display:'flex', gap:16, justifyContent:'center', marginTop:16, fontSize:13 }}>
        <div><span style={{ color:'#22c55e', fontWeight:700 }}>{promoters}</span> <span style={{ color:'#94a3b8' }}>Promotores</span></div>
        <div><span style={{ color:'#eab308', fontWeight:700 }}>{passives}</span> <span style={{ color:'#94a3b8' }}>Neutros</span></div>
        <div><span style={{ color:'#ef4444', fontWeight:700 }}>{detractors}</span> <span style={{ color:'#94a3b8' }}>Detractores</span></div>
      </div>
      <div style={{ fontSize:12, color:'#94a3b8', marginTop:8 }}>{total} respuestas</div>
    </div>
  );
}

function RatingBar({ label, value, max = 10 }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const color = pct >= 70 ? '#22c55e' : pct >= 50 ? '#eab308' : '#ef4444';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'6px 0' }}>
      <span style={{ flex:'0 0 160px', fontSize:13, color:'#475569' }}>{label}</span>
      <div style={{ flex:1, background:'#f1f5f9', borderRadius:8, height:20, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:8, transition:'width .5s ease' }} />
      </div>
      <span style={{ flex:'0 0 40px', textAlign:'right', fontSize:13, fontWeight:700, color }}>{value?.toFixed(1)}</span>
    </div>
  );
}

export default function SurveyResultsPage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter) params.type = filter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/survey/results', { params });
      if (res.data?.success) setData(res.data.data);
    } catch (e) {
      console.error('Error fetching survey results:', e);
    } finally {
      setLoading(false);
    }
  }, [filter, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calculate average ratings from recent responses
  const avgRatings = {};
  const ratingCounts = {};
  if (data?.recent) {
    data.recent.forEach(r => {
      const aspectsData = r.responses?.aspectos_rating;
      if (aspectsData && typeof aspectsData === 'object') {
        Object.entries(aspectsData).forEach(([key, val]) => {
          if (!avgRatings[key]) { avgRatings[key] = 0; ratingCounts[key] = 0; }
          avgRatings[key] += Number(val);
          ratingCounts[key]++;
        });
      }
    });
    Object.keys(avgRatings).forEach(k => {
      avgRatings[k] = ratingCounts[k] > 0 ? avgRatings[k] / ratingCounts[k] : 0;
    });
  }

  const allLabels = { ...NPS_LABELS.topping_frozen, ...NPS_LABELS.heladeria };
  const typeLabel = filter === 'topping_frozen' ? '🧊 Topping Frozen' : filter === 'heladeria' ? '🍦 Heladería' : '📊 Todas';

  return (
    <div style={{ padding:24, maxWidth:1200, margin:'0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:16, marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#1e293b', margin:0 }}>📊 Resultados de Encuestas</h1>
          <p style={{ fontSize:13, color:'#64748b', margin:0 }}>Análisis de satisfacción de clientes</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <select value={filter} onChange={e => setFilter(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13 }}>
            <option value="">Todas las encuestas</option>
            <option value="topping_frozen">🧊 Topping Frozen</option>
            <option value="heladeria">🍦 Heladería</option>
          </select>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13 }} />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13 }} />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>Cargando resultados...</div>
      ) : !data ? (
        <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>No hay datos disponibles</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:16, marginBottom:24 }}>
            <div style={{ background:'#fff', borderRadius:16, padding:20, border:'1px solid #e2e8f0', textAlign:'center' }}>
              <div style={{ fontSize:36, fontWeight:800, color:'#7c3aed' }}>{data.total}</div>
              <div style={{ fontSize:13, color:'#64748b' }}>Total Respuestas</div>
            </div>
            <div style={{ background:'#fff', borderRadius:16, padding:20, border:'1px solid #e2e8f0' }}>
              <NPSGauge score={data.nps?.score} promoters={data.nps?.promoters} passives={data.nps?.passives}
                detractors={data.nps?.detractors} total={data.nps?.total} />
            </div>
            {data.byType?.map(t => (
              <div key={t.survey_type} style={{ background:'#fff', borderRadius:16, padding:20, border:'1px solid #e2e8f0', textAlign:'center' }}>
                <div style={{ fontSize:36, fontWeight:800, color: t.survey_type === 'heladeria' ? '#E91E63' : '#8B2FC9' }}>{t.count}</div>
                <div style={{ fontSize:13, color:'#64748b' }}>{t.survey_type === 'heladeria' ? '🍦 Heladería' : '🧊 Topping Frozen'}</div>
              </div>
            ))}
          </div>

          {/* Rating Averages */}
          {Object.keys(avgRatings).length > 0 && (
            <div style={{ background:'#fff', borderRadius:16, padding:24, border:'1px solid #e2e8f0', marginBottom:24 }}>
              <h3 style={{ fontSize:16, fontWeight:700, color:'#1e293b', marginTop:0, marginBottom:16 }}>⭐ Calificación Promedio por Aspecto</h3>
              {Object.entries(avgRatings).sort((a, b) => b[1] - a[1]).map(([key, val]) => (
                <RatingBar key={key} label={allLabels[key] || key} value={val} max={10} />
              ))}
            </div>
          )}

          {/* NPS Distribution */}
          {data.nps?.distribution?.length > 0 && (
            <div style={{ background:'#fff', borderRadius:16, padding:24, border:'1px solid #e2e8f0', marginBottom:24 }}>
              <h3 style={{ fontSize:16, fontWeight:700, color:'#1e293b', marginTop:0, marginBottom:16 }}>📊 Distribución NPS</h3>
              <div style={{ display:'flex', gap:4, alignItems:'end', height:120 }}>
                {[0,1,2,3,4,5,6,7,8,9,10].map(score => {
                  const row = data.nps.distribution.find(r => r.nps_score === score);
                  const count = row ? Number(row.count) : 0;
                  const maxCount = Math.max(...data.nps.distribution.map(r => Number(r.count)), 1);
                  const h = count > 0 ? Math.max((count / maxCount) * 100, 10) : 4;
                  const color = score <= 6 ? '#ef4444' : score <= 8 ? '#eab308' : '#22c55e';
                  return (
                    <div key={score} style={{ flex:1, textAlign:'center' }}>
                      <div style={{ fontSize:10, color:'#64748b', marginBottom:2 }}>{count || ''}</div>
                      <div style={{ height:h, background:color, borderRadius:4, transition:'height .5s ease' }} />
                      <div style={{ fontSize:10, color:'#94a3b8', marginTop:2 }}>{score}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Responses */}
          <div style={{ background:'#fff', borderRadius:16, padding:24, border:'1px solid #e2e8f0' }}>
            <h3 style={{ fontSize:16, fontWeight:700, color:'#1e293b', marginTop:0, marginBottom:16 }}>💬 Respuestas Recientes</h3>
            {data.recent?.length === 0 ? (
              <p style={{ color:'#94a3b8', textAlign:'center', padding:24 }}>Sin respuestas aún</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {data.recent?.map(r => {
                  const openAnswers = [];
                  ['que_mejorar', 'que_destaca', 'comentarios', 'sabores_probados'].forEach(key => {
                    if (r.responses?.[key]) openAnswers.push(r.responses[key]);
                  });

                  return (
                    <div key={r.id} style={{ padding:16, background:'#f8fafc', borderRadius:12, border:'1px solid #f1f5f9' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                          <span style={{ fontSize:12, padding:'2px 8px', borderRadius:20, background: r.survey_type === 'heladeria' ? '#fce4ec' : '#ede9fe',
                            color: r.survey_type === 'heladeria' ? '#c2185b' : '#7c3aed', fontWeight:600 }}>
                            {r.survey_type === 'heladeria' ? '🍦 Heladería' : '🧊 Topping'}
                          </span>
                          {r.nps_score !== null && (
                            <span style={{ fontSize:12, fontWeight:700, color: r.nps_score >= 9 ? '#22c55e' : r.nps_score >= 7 ? '#eab308' : '#ef4444' }}>
                              NPS: {r.nps_score}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize:11, color:'#94a3b8' }}>
                          {new Date(r.created_at).toLocaleDateString('es-CO')}
                        </span>
                      </div>
                      {r.customer_name && (
                        <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>👤 {r.customer_name}</div>
                      )}
                      {openAnswers.length > 0 && (
                        <div style={{ fontSize:13, color:'#475569', lineHeight:1.5 }}>
                          {openAnswers.map((a, i) => (
                            <p key={i} style={{ margin:'4px 0', fontStyle:'italic' }}>"{a}"</p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* QR Print Links */}
          <div style={{ marginTop:24, display:'flex', gap:12, justifyContent:'center' }}>
            <a href="/encuesta-qr/topping" target="_blank" rel="noopener noreferrer"
              style={{ padding:'10px 20px', borderRadius:12, background:'#8B2FC9', color:'#fff', textDecoration:'none', fontSize:13, fontWeight:600 }}>
              🖨️ Imprimir QR Topping Frozen
            </a>
            <a href="/encuesta-qr/heladeria" target="_blank" rel="noopener noreferrer"
              style={{ padding:'10px 20px', borderRadius:12, background:'#E91E63', color:'#fff', textDecoration:'none', fontSize:13, fontWeight:600 }}>
              🖨️ Imprimir QR Heladería
            </a>
          </div>
        </>
      )}
    </div>
  );
}
